import config from 'constants/config'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { getCurrentUserId } from 'selectors/accountSelectors'

const TRIP_CHAT_MOBILE_ALLOWED_USER_IDS: ReadonlySet<string> = new Set([
  '15efccbc-a741-4dc4-8d3d-a86de738e941',
  'a2f8436d-5de0-4475-ba3b-9f7586095646',
  '531a988d-493c-42d9-a207-be00c448af9b',
  '309bbbeb-24c1-4929-becd-4e5e67c2b1d9',
  '942cc1f1-5481-4a28-b151-16fa6f928ec1',
  '5f34d7cb-3945-44f5-a2de-d3dcc828e825',
  '60720175-2221-47b3-ad00-e4d0a4a4f952',
  'd638c4cb-6a26-4088-9c1c-79897d2fbda0',
  '5fa9f9b4-c800-475e-875b-06c96b554992',
  'cf01cdf4-1019-4641-bf97-843fcd8fae15',
  'a311cbf5-16dd-4792-b934-fadcfbf5a107',
  '711f4d43-9387-4883-a6e2-8764f556e930',
])

// Temporary TRP-4081 rollout cohort for production/test mobile chat access.
// Remove once the mobile rollout moves to an Optimizely audience or opens beyond this cohort.
export function canUseTripChatMobile({
  currentUserId,
  isTripChatMobileEnabled,
}: {
  currentUserId?: string
  isTripChatMobileEnabled: boolean
}): boolean {
  if (!isTripChatMobileEnabled) {
    return false
  }

  return (
    !!currentUserId &&
    TRIP_CHAT_MOBILE_ALLOWED_USER_IDS.has(currentUserId)
  )
}

export function useCanAccessMobileChat(): boolean {
  const isTripChatMobileEnabled = useFeatureFlag(OptimizelyFeatureFlags.tripChatMobile)
  const currentUserId = useAppSelector(getCurrentUserId)

  if (!config.ENABLE_TRIP_PLANNER) {
    return false
  }

  return canUseTripChatMobile({
    currentUserId,
    isTripChatMobileEnabled,
  })
}

/**
 * On LE desktop we always route users to the chat trip planner. Mobile/tablet
 * chat access is gated by the mobile chat flag, with production/test access
 * further limited to the temporary rollout cohort. Brands without trip planner
 * get `false` so consumers hide any trip-planning UI entirely.
 */
export default function useShouldUseChat(): boolean {
  const isMobileOrTablet = useIsMobileOrTablet()
  const canAccessMobileChat = useCanAccessMobileChat()

  if (!config.ENABLE_TRIP_PLANNER) {
    return false
  }

  if (isMobileOrTablet) {
    return canAccessMobileChat
  }

  return true
}
